import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import PaymentBanner from "./PaymentBanner";
import PaymentServices from "../../services/PaymentServices";

function PaymentBannerWrapper({ children }) {
  const location = useLocation();
  const [needsPayment, setNeedsPayment] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Skip payment check on login/register pages (no token available)
    if (location.pathname === '/login' ||
      location.pathname === '/selectregister' ||
      location.pathname.includes('/register') ||
      location.pathname.includes('/forgot-password') ||
      location.pathname.includes('/reset-password')) {
      setNeedsPayment(false);
      setPaymentDetails(null);
      return;
    }
    checkPaymentStatus();
  }, [location.pathname]);

  useEffect(() => {
    // Reset dismissed state on route change (but keep payment status)
    setIsDismissed(false);
  }, [location.pathname]);

  async function checkPaymentStatus() {
    const token = localStorage.getItem("token");
    if (!token) {
      setNeedsPayment(false);
      setPaymentDetails(null);
      return;
    }

    try {
      // First check localStorage for payment details from login
      const storedNeedsPayment = localStorage.getItem("needsPayment");
      const storedPaymentDetails = localStorage.getItem("paymentDetails");

      if (storedNeedsPayment === "true" && storedPaymentDetails) {
        try {
          const parsedDetails = JSON.parse(storedPaymentDetails);
          console.log("Payment details from localStorage:", parsedDetails);
          // Check isPaid field first (new backend field)
          // If isPaid === false, payment is needed
          // Otherwise, check for valid amount (legacy support)
          const needsPayment = parsedDetails.isPaid === false ||
            (parsedDetails.isPaid !== true &&
              ((parsedDetails.amount && parsedDetails.amount > 0) ||
                (parsedDetails.totalAmount && parsedDetails.totalAmount > 0) ||
                (parsedDetails.amountDue && parsedDetails.amountDue > 0)));
          if (needsPayment) {
            setNeedsPayment(true);
            setPaymentDetails(parsedDetails);
            return;
          } else {
            console.warn("Payment details found but payment not needed:", parsedDetails);
          }
        } catch (e) {
          console.error("Error parsing stored payment details:", e);
        }
      }

      // Call payment status API to get latest status
      const response = await PaymentServices.checkPaymentStatus();

      // If response is null, endpoint doesn't exist - use localStorage fallback
      if (response === null) {
        // Endpoint doesn't exist, rely on localStorage from login
        if (storedNeedsPayment === "true" && storedPaymentDetails) {
          try {
            const parsedDetails = JSON.parse(storedPaymentDetails);
            console.log("Using localStorage payment details (API unavailable):", parsedDetails);
            // Check isPaid field first (new backend field)
            const needsPayment = parsedDetails.isPaid === false ||
              (parsedDetails.isPaid !== true &&
                ((parsedDetails.amount && parsedDetails.amount > 0) ||
                  (parsedDetails.totalAmount && parsedDetails.totalAmount > 0) ||
                  (parsedDetails.amountDue && parsedDetails.amountDue > 0)));
            if (needsPayment) {
              setNeedsPayment(true);
              setPaymentDetails(parsedDetails);
              return;
            } else {
              console.warn("Payment details in localStorage but payment not needed:", parsedDetails);
            }
          } catch (e) {
            console.error("Error parsing stored payment details:", e);
          }
        }
        // No stored payment details, no payment needed
        setNeedsPayment(false);
        setPaymentDetails(null);
        return;
      }

      // Check if payment is needed - use isPaid field if available, otherwise use needsPayment flag
      const paymentNeeded = response.isPaid === false ||
        (response.isPaid !== true && response.needsPayment);

      if (paymentNeeded) {
        setNeedsPayment(true);
        // Use payment details from API response
        const details = response.payment || response.paymentDetails || {
          isPaid: response.isPaid,
          amountPaid: response.amountPaid || 0,
          amountDue: response.amountDue || response.displayAmount || response.totalAmount || response.amount || 0,
          displayAmount: response.displayAmount || response.amountDue || response.totalAmount || response.amount || 0,
          amount: response.amount,
          totalAmount: response.totalAmount || response.amount,
          planType: response.planType || "annual",
          installmentsTotal: response.installmentsTotal || 0,
          installmentAmount: response.installmentAmount || 0,
          installmentsPaid: response.installmentsPaid || 0,
        };
        setPaymentDetails(details);
        // Update localStorage
        localStorage.setItem("needsPayment", "true");
        localStorage.setItem("paymentDetails", JSON.stringify(details));
      } else {
        setNeedsPayment(false);
        setPaymentDetails(null);
        // Clear localStorage
        localStorage.removeItem("needsPayment");
        localStorage.removeItem("paymentDetails");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      // On error, check localStorage as fallback
      const storedNeedsPayment = localStorage.getItem("needsPayment");
      const storedPaymentDetails = localStorage.getItem("paymentDetails");
      if (storedNeedsPayment === "true" && storedPaymentDetails) {
        try {
          const parsedDetails = JSON.parse(storedPaymentDetails);
          console.log("Error fallback - using localStorage payment details:", parsedDetails);
          // Check isPaid field first (new backend field)
          const needsPayment = parsedDetails.isPaid === false ||
            (parsedDetails.isPaid !== true &&
              ((parsedDetails.amount && parsedDetails.amount > 0) ||
                (parsedDetails.totalAmount && parsedDetails.totalAmount > 0) ||
                (parsedDetails.amountDue && parsedDetails.amountDue > 0)));
          if (needsPayment) {
            setNeedsPayment(true);
            setPaymentDetails(parsedDetails);
          } else {
            console.warn("Payment details in localStorage but payment not needed:", parsedDetails);
            setNeedsPayment(false);
            setPaymentDetails(null);
          }
        } catch (e) {
          console.error("Error parsing stored payment details:", e);
        }
      }
    }
  }

  const handleDismiss = () => {
    // Dismiss for current session only
    setIsDismissed(true);
    // Banner will reappear on next page load/refresh
  };

  // Don't show banner on public pages or payment page
  const publicPages = [
    "/login",
    "/register",
    "/selectregister",
    "/forgot-password",
    "/reset-password",
    "/payment",
    "/",
    "/home",
  ];

  if (publicPages.includes(location.pathname) || location.pathname.startsWith("/register/") || location.pathname.startsWith("/schoolregister/") || location.pathname.startsWith("/teacherregister/")) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Non-blocking payment banner */}
      {needsPayment && paymentDetails && !isDismissed && (
        <PaymentBanner
          paymentDetails={paymentDetails}
          onDismiss={handleDismiss}
        />
      )}
      {children}
    </>
  );
}

PaymentBannerWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PaymentBannerWrapper;

